import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { AuthContext } from '../auth/AuthContext';
import { createIssue, getEpicsForProject } from '../api/issueApi';
import { getProjects, getMyProjects } from '../api/projectApi';
import { getSprints } from '../api/sprintApi';
import MentionComposer from './MentionComposer';
import EtaQuickPicker from './EtaQuickPicker';
import { filterEpicsForSprintPicker } from '../utils/epicSprintFilter';
import { filterSprintsForIssuePicker } from '../constants/sprintAccess';
import { resolveAssigneeIdsFromText } from '../utils/dailyNotebookMentions';
import {
  normalizeProjectMembersResponse,
  mergeTeamsForDepartment,
  filterAssignableUsersByTeam,
} from '../utils/assigneeTeamFilter';
import { getCustomTeams } from '../api/configApi';
import { DEFAULT_DEPARTMENT } from '../constants/departments';
import { COLORS as C, RADIUS } from '../constants/ui';

/** Mirrors IssueForm's list normalizer — these endpoints vary in envelope. */
const toArray = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const labelStyle = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: C.inkSoft,
  marginBottom: 4,
};

const selectStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: RADIUS.control,
  border: `1px solid ${C.line}`,
  fontSize: '0.85rem',
  background: '#fff',
  color: C.ink,
  boxSizing: 'border-box',
};

/**
 * Story creation, reduced to what actually has to be decided up front:
 * where it goes (project/sprint/epic), what it is, and who owns it by when.
 *
 * Everything the long form asked for is either derived or defaulted:
 *   - assignee + team    -> from the @mention
 *   - type               -> Story (tasks are added in the next step)
 *   - priority           -> High, the existing default
 *   - story points       -> 0; ETA is how this team measures commitment
 *   - description/links  -> added later in the Story detail modal
 *
 * The backend only requires projectid, issuetype and assigneeteam, so nothing
 * here changes the API contract — the omitted fields still go up as defaults.
 */
export default function CompactStoryForm({
  projectId,
  sprintId,
  sprintsEnabled = true,
  preferAssignedProjects = false,
  defaultEpicId = null,
  onCreated,
}) {
  const [form, setForm] = useState({
    projectid: projectId || '',
    sprint_id: sprintId || '',
    parent_id: defaultEpicId || '',
  });
  const [text, setText] = useState('');
  const [eta, setEta] = useState('');
  const [team, setTeam] = useState('Any');
  const [assigneeId, setAssigneeId] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const { role, user } = useContext(AuthContext);

  // Same query keys as IssueForm so the cache is shared, not duplicated.
  const { data: projectsData } = useQuery({
    queryKey: preferAssignedProjects ? ['projects', 'my'] : ['projects', 'all'],
    queryFn: () =>
      (preferAssignedProjects ? getMyProjects() : getProjects()).then((r) => r.data),
  });
  const projects = toArray(projectsData, ['projects', 'rows', 'result']);

  const { data: sprintsData } = useQuery({
    queryKey: ['sprints', form.projectid],
    queryFn: () => getSprints(form.projectid || null).then((r) => r.data),
    enabled: !!form.projectid && sprintsEnabled,
  });
  // Sprint visibility is access-controlled — never show the raw list.
  const sprints = filterSprintsForIssuePicker(
    toArray(sprintsData, ['sprints', 'rows', 'result']),
    role,
    user?.permissions,
  );

  const { data: epicsData = [] } = useQuery({
    queryKey: ['project-epics', form.projectid],
    queryFn: () => getEpicsForProject(form.projectid).then((r) => r.data),
    enabled: !!form.projectid,
  });
  const epics = useMemo(
    () => filterEpicsForSprintPicker(toArray(epicsData, ['rows', 'result']), form.sprint_id),
    [epicsData, form.sprint_id],
  );

  const { data: membersRaw } = useQuery({
    queryKey: ['project-members', form.projectid],
    queryFn: () => api.get(`/projects/${form.projectid}/members`).then((r) => r.data),
    enabled: !!form.projectid,
  });
  const members = useMemo(() => normalizeProjectMembersResponse(membersRaw), [membersRaw]);

  // An epic that no longer belongs to the chosen sprint must not stay selected,
  // or the create call fails validation on a parent the user can't even see.
  useEffect(() => {
    if (form.parent_id && !epics.some((e) => String(e.issueid) === String(form.parent_id))) {
      set('parent_id', '');
    }
  }, [epics, form.parent_id]);

  const { data: customTeams = [] } = useQuery({
    queryKey: ['custom-teams'],
    queryFn: getCustomTeams,
  });
  const teamOptions = useMemo(
    () => mergeTeamsForDepartment(customTeams, user?.department || DEFAULT_DEPARTMENT),
    [customTeams, user?.department],
  );

  // Admins/superadmins aren't work assignees — same rule as the long form.
  const baseAssignableUsers = useMemo(
    () => members.filter((m) => !['admin', 'superadmin'].includes(m.role?.toLowerCase())),
    [members],
  );
  const assignableUsers = useMemo(
    () => filterAssignableUsersByTeam(baseAssignableUsers, team, teamOptions),
    [baseAssignableUsers, team, teamOptions],
  );

  const mentionIds = useMemo(
    () => resolveAssigneeIdsFromText(text, members),
    [text, members],
  );

  /**
   * The @mention and the dropdown are two views of one decision, so typing
   * "@Priya" fills the dropdowns and picking from the dropdowns is equally
   * valid. The mention wins when present because it's the more explicit act —
   * you named a person in the sentence itself.
   */
  useEffect(() => {
    const id = mentionIds[0];
    if (id == null) return;
    const m = members.find((x) => Number(x.id) === Number(id));
    if (!m) return;
    setAssigneeId(String(m.id));
    if (m.team) setTeam(m.team);
  }, [mentionIds, members]);

  // Drop a selection the current team filter no longer offers, so the visible
  // dropdown and the submitted value can never disagree.
  useEffect(() => {
    if (!assigneeId) return;
    if (!assignableUsers.some((u) => String(u.id) === String(assigneeId))) {
      setAssigneeId('');
    }
  }, [assignableUsers, assigneeId]);

  const assignee = members.find((m) => Number(m.id) === Number(assigneeId));

  const mutation = useMutation({
    mutationFn: () =>
      createIssue({
        projectid: form.projectid,
        sprint_id: form.sprint_id || null,
        issuetype: 'Story',
        description: text.trim(),
        assigneeteam: assignee?.team || team || 'Any',
        priority: 'High',
        assignee_id: assignee?.id ?? null,
        story_points: 0,
        due_date: eta || null,
        parent_id: form.parent_id || null,
      }),
    onSuccess: (res) => onCreated?.(res?.data?.issue ?? res?.data),
    onError: (err) =>
      toast.error(err.response?.data?.message || 'Failed to create story'),
  });

  const submit = () => {
    const trimmed = text.trim();
    if (!form.projectid) return toast.error('Pick a project');
    if (!trimmed) return toast.error('Write what needs to be done');
    // An owner is required, but either route counts — the @mention or the
    // dropdown. Only complain about an unmatched @ when it's the sole attempt.
    if (!assignee) {
      if (trimmed.includes('@')) {
        return toast.error('Could not match @name to a project member — pick from the @ list');
      }
      return toast.error('Tag the owner with @ or pick one below');
    }
    mutation.mutate();
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: sprintsEnabled ? '1fr 1fr' : '1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Project *</label>
          <select
            value={form.projectid}
            onChange={(e) => {
              set('projectid', e.target.value);
              set('sprint_id', '');
              set('parent_id', '');
            }}
            style={selectStyle}
          >
            <option value="">Select project…</option>
            {projects.map((p) => (
              <option key={p.projectid} value={p.projectid}>
                {p.projectname || p.projectid}
              </option>
            ))}
          </select>
        </div>

        {sprintsEnabled && (
          <div>
            <label style={labelStyle}>Sprint</label>
            <select
              value={form.sprint_id}
              onChange={(e) => set('sprint_id', e.target.value)}
              style={selectStyle}
              disabled={!form.projectid}
            >
              <option value="">— Backlog —</option>
              {sprints.map((s) => (
                <option key={s.sprint_id} value={s.sprint_id}>
                  {s.sprint_name}
                  {s.status ? ` (${s.status})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {epics.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>
            Epic <span style={{ fontWeight: 400, color: C.inkFaint }}>(optional)</span>
          </label>
          <select
            value={form.parent_id}
            onChange={(e) => set('parent_id', e.target.value)}
            style={selectStyle}
          >
            <option value="">— None —</option>
            {epics.map((ep) => (
              <option key={ep.issueid} value={ep.issueid}>
                #{ep.issueid} · {(ep.description || 'Epic').split('\n')[0]}
              </option>
            ))}
          </select>
        </div>
      )}

      <label style={labelStyle}>What needs to be done? *</label>
      <MentionComposer
        value={text}
        onChange={setText}
        onSubmit={submit}
        members={members}
        disabled={mutation.isPending || !form.projectid}
        placeholder={
          form.projectid ? 'Ship the pricing page @Name…' : 'Pick a project first…'
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '10px 0' }}>
        <div>
          <label style={labelStyle}>Team</label>
          <select
            value={team}
            onChange={(e) => {
              setTeam(e.target.value);
              // The chosen person may not be in the new team; make them re-pick
              // rather than silently submitting a mismatched pair.
              setAssigneeId('');
            }}
            style={selectStyle}
            disabled={mutation.isPending}
          >
            {teamOptions.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Owner *</label>
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            style={selectStyle}
            disabled={mutation.isPending || !form.projectid}
          >
            <option value="">
              {assignableUsers.length === 0 ? 'No members in this team' : 'Select person…'}
            </option>
            {assignableUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.email}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>ETA</label>
        <EtaQuickPicker value={eta} onChange={setEta} disabled={mutation.isPending} />
      </div>

      <div style={{ fontSize: '0.7rem', color: C.inkFaint, marginBottom: 12 }}>
        {assignee ? (
          <>Owner: <strong style={{ color: C.accentInk }}>{assignee.full_name}</strong> · team {assignee.team || team}</>
        ) : (
          'Tag with @ or pick an owner above — required'
        )}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={mutation.isPending}
        style={{
          width: '100%',
          padding: '11px',
          borderRadius: RADIUS.control,
          border: 'none',
          background: C.accentInk,
          color: '#fff',
          fontWeight: 700,
          fontSize: '0.9rem',
          cursor: mutation.isPending ? 'not-allowed' : 'pointer',
          opacity: mutation.isPending ? 0.7 : 1,
        }}
      >
        {mutation.isPending ? 'Creating…' : 'Create Story'}
      </button>
    </div>
  );
}
